import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./CustomApplications.css";

const CONTACT = "/contact?service=Custom%20Web%20Application%20Development";

const apps = [
  ["01", "dash", "Business Dashboards", "Centralize the information your team needs."],
  ["02", "tools", "Internal Tools", "Replace repetitive manual processes with purpose-built systems."],
  ["03", "portal", "Customer Portals", "Give customers a secure place to manage information and actions."],
  ["04", "admin", "Admin Platforms", "Manage users, content, records, workflows, and permissions."],
  ["05", "book", "Booking & Scheduling", "Build custom scheduling experiences around your business rules."],
  ["06", "data", "Data Management", "Structure and manage the information your application depends on."],
  ["07", "auto", "Workflow Automation", "Turn repetitive steps into connected digital processes."],
  ["08", "saas", "Custom SaaS", "Build a product around a specific market or business model."]
];

const rules = [
  ["when", "When", "New customer submits request"],
  ["check", "Check", "Account status"],
  ["if", "If", "Approved"],
  ["then", "Then", "Create record + notify team"],
  ["else", "Otherwise", "Request additional information"]
];

const ruleNotes = {
  when: "A request enters the application and becomes a record the workflow can act on.",
  check: "The application reads the account before it decides which path to take.",
  if: "The condition is the business rule, not a generic status pasted onto a form.",
  then: "When the rule matches, the record is created and the team is notified.",
  else: "When it does not match, the application asks for what is still missing."
};

const layers = [
  ["data", "Database"],
  ["auth", "Authentication"],
  ["api", "APIs"],
  ["pay", "Payments"],
  ["note", "Notifications"],
  ["stats", "Analytics"],
  ["third", "Third-party systems"]
];

const capabilities = [
  ["wide", "auth", "Authentication & Accounts", "Sign-in and a record for each person who uses the product."],
  ["", "roles", "Role-Based Permissions", "Access follows the role, so people see the actions that belong to them."],
  ["wide", "report", "Dashboards & Reporting", "The records the team needs, gathered in one working view."],
  ["", "forms", "Forms & Workflows", "A request moves through the steps the business actually uses."],
  ["", "search", "Search & Filtering", "Find a record by the fields the operation depends on."],
  ["", "notify", "Notifications", "The right person hears about a step when the workflow reaches them."],
  ["", "pay", "Payments", "A payment step where the workflow needs one and the provider allows it."],
  ["", "api", "API Integrations", "A connection to another system when that system offers one."],
  ["", "files", "File Management", "Documents stay with the record they belong to."],
  ["", "records", "Data Management", "The structure of the information the application stores and updates."],
  ["wide", "flow", "Automation", "A repeated step runs from the rule instead of being passed along by hand."],
  ["", "admin", "Admin Controls", "The tools a team uses to manage users, records, and the workflow."]
];

const process = [
  ["Understand", "Learn the workflow, the people, the data, and the constraints before deciding what to build."],
  ["Map", "Define the roles, the records, and the path a task takes through the system."],
  ["Design", "Shape the screens, the states, and the actions people will use."],
  ["Build", "Develop the application, the rules, and the data it depends on."],
  ["Integrate", "Connect the systems the workflow needs, where those systems allow a connection."],
  ["Test", "Check roles, workflows, data, and the paths through the product."],
  ["Launch", "Deploy the application and verify it in the production environment."],
  ["Improve", "Continue refining the product as the workflow and the business change."]
];

const screens = [
  ["board", "Dashboard"],
  ["records", "Records"],
  ["flow", "Workflow"],
  ["detail", "Detail"],
  ["settings", "Settings"]
];

const additions = ["New workflow", "New user role", "New integration", "New feature", "New data"];

const principles = [
  ["Business First", "We design around the actual workflow rather than forcing a generic process.", "work"],
  ["Purposeful", "Every feature should solve a real problem.", "aim"],
  ["Connected", "Applications can work with the systems your business already depends on.", "link"],
  ["Ready to Evolve", "Build the foundation so the product can grow and change over time.", "grow"]
];

const faqs = [
  ["What is a custom web application?", "A web application is software people use in a browser to complete a defined job: managing records, running a workflow, or giving customers a place to act. It is built around that job, rather than assembled as a general website."],
  ["When should a business consider custom software?", "When the work has rules, roles, or data that packaged tools do not fit. If an existing product already matches the workflow, that is usually the simpler path."],
  ["Can you connect the application to existing systems?", "Yes, where those systems offer an API or another supported connection. What can be connected depends on what each system allows."],
  ["Can you build user accounts and permissions?", "Yes. Accounts, sign-in, and roles can be part of the application so people see the records and actions their role allows."],
  ["Can you create admin dashboards?", "Yes. An admin area can cover the records, users, and workflows a team needs to run the product."],
  ["Can you integrate APIs and third-party services?", "Yes, where the other service provides an API or a supported connection. Typical cases are payments, notifications, and analytics. What is possible depends on that service."],
  ["Can you automate business workflows?", "Yes. Repeated steps can be connected so a request, a check, and a follow-up happen in the application. The rules follow the business process."],
  ["Can the application be expanded later?", "Yes. New workflows, roles, and integrations can be added after the first release. That later work is scoped separately from the initial build."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`ca-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="ca-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function AppWorkspace() {
  const [nav, setNav] = useState("overview");
  const items = [
    ["overview", "Overview"],
    ["records", "Records"],
    ["flow", "Workflow"],
    ["team", "Team"]
  ];
  return (
    <div className="ca-app" aria-hidden="true">
      <aside className="ca-side">
        <strong>Fielddesk</strong>
        {items.map(([id, label]) => (
          <button key={id} type="button" className={nav === id ? "is-on" : ""} onMouseEnter={() => setNav(id)} onFocus={() => setNav(id)}>
            {label}
          </button>
        ))}
      </aside>
      <div className="ca-main">
        <header className="ca-top">
          <b>Operations</b>
          <span className="ca-user">AL</span>
        </header>
        <div className="ca-metrics">
          <div><small>Open</small><strong>12</strong></div>
          <div><small>In review</small><strong>4</strong></div>
          <div className="is-live"><small>Ready</small><strong>7</strong></div>
        </div>
        <div className="ca-split">
          <div className="ca-table">
            <div className="ca-table-head"><span>Request</span><span>Owner</span><span>Status</span></div>
            <div><span>Site survey</span><span>M. Chen</span><em>Review</em></div>
            <div><span>Access change</span><span>A. Lopez</span><em className="is-ready">Ready</em></div>
            <div><span>New account</span><span>J. Patel</span><em>Open</em></div>
          </div>
          <div className="ca-activity">
            <b>Activity</b>
            <span>Record updated</span>
            <span>Rule matched</span>
            <div className="ca-bars" aria-hidden="true"><i /><i /><i /><i /><i /></div>
          </div>
        </div>
        <div className="ca-toast">Request assigned to review</div>
      </div>
    </div>
  );
}

function WorkflowStudio() {
  const [active, setActive] = useState("task");
  const zones = [
    ["flow", "Workflow", "Intake, review, and handoff."],
    ["task", "Active task", "The step someone is working on now."],
    ["data", "Data", "The record that step reads and updates."],
    ["tool", "Connected tool", "A system the workflow can reach."]
  ];
  return (
    <div className={`ca-studio ${active ? "is-hot" : ""}`}>
      <div className="ca-studio-flow">
        {["Intake", "Review", "Approve", "Notify"].map((step, index) => (
          <button key={step} type="button" className={index === 1 ? "is-on" : ""} onMouseEnter={() => setActive("flow")} onFocus={() => setActive("flow")}>
            <em>{String(index + 1).padStart(2, "0")}</em>
            {step}
          </button>
        ))}
      </div>
      <button type="button" className={`ca-studio-task ${active === "task" ? "is-on" : ""}`} onMouseEnter={() => setActive("task")} onFocus={() => setActive("task")}>
        <small>In progress</small>
        <strong>Review the account</strong>
        <span>Assigned to operations. Waiting on the account record.</span>
      </button>
      <div className="ca-studio-side">
        <button type="button" className={active === "data" ? "is-on" : ""} onMouseEnter={() => setActive("data")} onFocus={() => setActive("data")}>
          <b>Account record</b>
          <span>Status, owner, last update</span>
        </button>
        <button type="button" className={active === "tool" ? "is-on" : ""} onMouseEnter={() => setActive("tool")} onFocus={() => setActive("tool")}>
          <b>External system</b>
          <span>Connected when that system allows it</span>
        </button>
      </div>
      <p className="ca-caption">{zones.find(([id]) => id === active)?.[2]}</p>
    </div>
  );
}

function Mini({ kind }) {
  if (kind === "dash") {
    return (
      <div className="ca-mini ca-ui ca-mini-dash" aria-hidden="true">
        <header><b>Overview</b><span>This week</span></header>
        <div className="ca-mini-stats">
          <div><small>Activity</small><i style={{ "--h": "72%" }} /></div>
          <div><small>Orders</small><i style={{ "--h": "46%" }} /></div>
          <div><small>Queue</small><i style={{ "--h": "60%" }} /></div>
        </div>
        <div className="ca-mini-chart"><i /><i /><i /><i /><i /><i /><i /></div>
      </div>
    );
  }
  if (kind === "tools") {
    return (
      <div className="ca-mini ca-ui ca-mini-list" aria-hidden="true">
        <header><b>Tasks</b><span>+</span></header>
        <ul>
          <li className="is-done"><i /><span>Review request</span></li>
          <li><i /><span>Assign team member</span></li>
          <li><i /><span>Complete approval</span></li>
        </ul>
      </div>
    );
  }
  if (kind === "portal") {
    return (
      <div className="ca-mini ca-ui ca-mini-portal" aria-hidden="true">
        <header><b><i />Account</b></header>
        <div className="ca-mini-links"><span>Profile</span><span>Settings</span></div>
        <small>Upcoming</small>
        <em />
        <strong>View details</strong>
      </div>
    );
  }
  if (kind === "admin") {
    return (
      <div className="ca-mini ca-ui ca-mini-admin" aria-hidden="true">
        <header><b>Users</b><span>Add</span></header>
        <ul>
          <li><i>A</i><span>Alex</span><em>Admin</em></li>
          <li><i>J</i><span>Jordan</span><em>Editor</em></li>
          <li><i>T</i><span>Taylor</span><em>Viewer</em></li>
        </ul>
      </div>
    );
  }
  if (kind === "book") {
    return (
      <div className="ca-mini ca-ui ca-mini-book" aria-hidden="true">
        <header><b>Schedule</b></header>
        <div className="ca-mini-slots">
          <div><small>9:00</small><i /></div>
          <div className="is-on"><small>10:00</small><i /><b>Booked</b></div>
          <div><small>11:00</small><i /></div>
        </div>
      </div>
    );
  }
  if (kind === "data") {
    return (
      <div className="ca-mini ca-ui ca-mini-data" aria-hidden="true">
        <header><b>Records</b><span>Search</span></header>
        <div className="is-head"><span>ID</span><span>Name</span><span>Status</span></div>
        <div><span>01</span><span>Client</span><em>Active</em></div>
        <div><span>02</span><span>Request</span><em className="is-wait">Pending</em></div>
        <div><span>03</span><span>Order</span><em>Active</em></div>
      </div>
    );
  }
  if (kind === "auto") {
    return (
      <div className="ca-mini ca-ui ca-mini-auto" aria-hidden="true">
        <span><i />New request</span>
        <b />
        <span className="is-on"><i />Check</span>
        <b />
        <span><i />Notify</span>
      </div>
    );
  }
  return (
    <div className="ca-mini ca-ui ca-mini-saas" aria-hidden="true">
      <header><b>Workspace</b><span>Account</span></header>
      <div className="ca-mini-app">
        <aside>
          <span className="is-on">Home</span>
          <span>Projects</span>
          <span>Reports</span>
          <span>Settings</span>
        </aside>
        <div>
          <strong>Overview</strong>
          <div className="ca-mini-pair"><i /><i /></div>
        </div>
      </div>
    </div>
  );
}

function RulesEngine() {
  const [active, setActive] = useState("then");
  return (
    <div className="ca-rules-wrap">
      <div className="ca-rules">
        {rules.map(([id, label, text], index) => (
          <React.Fragment key={id}>
            {index > 0 ? <span className={`ca-rule-join ${active === id ? "is-hot" : ""}`} aria-hidden="true" /> : null}
            <button
              type="button"
              className={`ca-rule is-${id} ${active === id ? "is-hot" : ""}`}
              onMouseEnter={() => setActive(id)}
              onFocus={() => setActive(id)}
            >
              <small>{label}</small>
              <strong>{text}</strong>
            </button>
          </React.Fragment>
        ))}
      </div>
      <p className="ca-rule-note">{ruleNotes[active]}</p>
    </div>
  );
}

function Architecture() {
  const [active, setActive] = useState("api");
  return (
    <div className={`ca-arch is-${active}`}>
      <div className="ca-arch-row">
        {layers.slice(0, 3).map(([id, label]) => (
          <button key={id} type="button" className={active === id ? "is-hot" : ""} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)}>{label}</button>
        ))}
      </div>
      <div className="ca-arch-core">
        <small>System</small>
        <strong>Custom application</strong>
        <span>Rules, users, and records in one product.</span>
      </div>
      <div className="ca-arch-row is-wide">
        {layers.slice(3).map(([id, label]) => (
          <button key={id} type="button" className={active === id ? "is-hot" : ""} onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)}>{label}</button>
        ))}
      </div>
    </div>
  );
}

function CapabilityMark({ kind }) {
  if (kind === "auth") return <i className="ca-cap-mark is-auth" />;
  if (kind === "roles") return <i className="ca-cap-mark is-roles" />;
  if (kind === "report") return <i className="ca-cap-mark is-report" />;
  if (kind === "flow") return <i className="ca-cap-mark is-flow" />;
  return <i className={`ca-cap-mark is-${kind}`} />;
}

function ProcessViz({ index }) {
  if (index === 0) {
    return <ul className="ca-viz ca-viz-list">{["Workflow", "People", "Data", "Constraints"].map((label) => <li key={label}>{label}</li>)}</ul>;
  }
  if (index === 1) {
    return (
      <div className="ca-viz ca-viz-map">
        <span>Role</span><i /><span>Record</span><i /><span>Step</span>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="ca-viz ca-viz-ui">
        <aside><b /><b className="is-on" /><b /></aside>
        <div><strong>Review</strong><em /><em className="is-short" /></div>
      </div>
    );
  }
  if (index === 3) {
    return <ul className="ca-viz ca-viz-stack">{["Interface", "Rules", "Data"].map((label, step) => <li key={label}><em>{String(step + 1).padStart(2, "0")}</em>{label}</li>)}</ul>;
  }
  if (index === 4) {
    return (
      <div className="ca-viz ca-viz-link">
        <span>Application</span>
        <div><b>Records</b><b>Accounts</b><b>Messages</b></div>
      </div>
    );
  }
  if (index === 5) {
    return <ul className="ca-viz ca-viz-checks">{["Roles", "Workflow", "Data", "Paths"].map((label) => <li key={label}><i />{label}</li>)}</ul>;
  }
  if (index === 6) {
    return (
      <div className="ca-viz ca-viz-launch">
        <div><small>Prepare</small><strong>Staging</strong></div>
        <span>Launch</span>
        <div className="is-live"><small>Live</small><strong>Production</strong></div>
      </div>
    );
  }
  return <ul className="ca-viz ca-viz-list">{["Review the workflow", "Adjust a rule", "Release the change"].map((label) => <li key={label}>{label}</li>)}</ul>;
}

function ProcessTimeline() {
  const [active, setActive] = useState(3);
  const refs = useRef([]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;
    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };
    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);
  const [title, text] = process[active];
  return (
    <>
      <div className="ca-process" style={{ "--ca-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="ca-process-rail" />
        <div className="ca-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`ca-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="ca-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="ca-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="ca-build-stage" aria-live="polite">
        <div className="ca-build-stage-body" key={active}>
          <p className="ca-build-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="ca-build-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function ScreenFace({ view }) {
  if (view === "records") {
    return (
      <div className="ca-screen">
        <div className="ca-screen-row is-head"><span>Record</span><span>Owner</span><span>State</span></div>
        <div className="ca-screen-row"><span>Request 184</span><span>Operations</span><em>Open</em></div>
        <div className="ca-screen-row"><span>Request 185</span><span>Review</span><em>Waiting</em></div>
        <div className="ca-screen-row"><span>Request 186</span><span>Accounts</span><em className="is-ready">Ready</em></div>
      </div>
    );
  }
  if (view === "flow") {
    return (
      <div className="ca-screen ca-screen-flow">
        {["Submitted", "Checked", "Approved", "Notified"].map((step, index) => (
          <React.Fragment key={step}>
            {index > 0 ? <i /> : null}
            <span className={index === 2 ? "is-on" : ""}>{step}</span>
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (view === "detail") {
    return (
      <div className="ca-screen ca-screen-detail">
        <strong>Request 184</strong>
        <p>Account review for a new customer record.</p>
        <div><span>Owner</span><b>Operations</b></div>
        <div><span>State</span><b>In review</b></div>
        <button type="button">Update record</button>
      </div>
    );
  }
  if (view === "settings") {
    return (
      <div className="ca-screen ca-screen-settings">
        <div><span>Roles</span><b>Admin, reviewer, customer</b></div>
        <div><span>Notifications</span><b>On for assigned steps</b></div>
        <div><span>Access</span><b>Limited by role</b></div>
      </div>
    );
  }
  return (
    <div className="ca-screen ca-screen-board">
      <div><small>Open</small><strong>12</strong></div>
      <div><small>Review</small><strong>4</strong></div>
      <div><small>Ready</small><strong>7</strong></div>
    </div>
  );
}

function AppStates() {
  const [view, setView] = useState("board");
  return (
    <div className="ca-states">
      <div className="ca-state-nav" role="tablist" aria-label="Application screens">
        {screens.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} className={view === id ? "is-on" : ""} onClick={() => setView(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="ca-state-frame" key={view}>
        <header><b>Fielddesk</b><span>{screens.find(([id]) => id === view)?.[1]}</span></header>
        <ScreenFace view={view} />
      </div>
    </div>
  );
}

function PrincipleMark({ kind }) {
  if (kind === "work") return <div className="ca-mark"><span /><span /><span className="is-on" /></div>;
  if (kind === "aim") return <div className="ca-mark is-aim"><b /></div>;
  if (kind === "link") return <div className="ca-mark is-link"><i /><i /><i /></div>;
  return <div className="ca-mark is-grow"><em /><em /><em /></div>;
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`ca-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button type="button" className="w-full flex items-center justify-between gap-6 py-3.5 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : index)}>
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="ca-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`ca-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div><p className="pb-5 text-slate-600 leading-relaxed">{answer}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CustomApplications() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Custom Web Applications | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  return (
    <article data-testid="service-detail-page" className="ca-page bg-white">
      <header className="ca-hero border-b border-slate-200">
        <div className="ca-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="ca-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="ca-hero-item ca-kicker" style={{ animationDelay: "120ms" }}>
              <span>06</span>
              <span>Custom Web Applications</span>
            </p>
            <h1 className="ca-hero-item text-4xl sm:text-5xl lg:text-[3.15rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Software Built Around<span className="hidden xl:inline"><br /></span> the Way You Work.
            </h1>
            <p className="ca-hero-item ca-lead text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Custom web applications designed around your workflows, your users, and the systems your business depends on.
            </p>
            <div className="ca-hero-item ca-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-custom-applications" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <AppWorkspace />
          </div>
        </div>
      </header>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="lg:order-2">
            <Reveal>
              <Kicker index="01" label="The application" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Your Workflow Deserves Its Own System.</h2>
              <p className="ca-lead text-slate-600 leading-relaxed">
                Off-the-shelf software can force your business to work around someone else&apos;s process. Custom applications let the software adapt to the way your team actually works.
              </p>
            </Reveal>
          </div>
          <WorkflowStudio />
        </div>
      </section>

      <section className="ca-section border-b border-slate-200 bg-slate-50/70">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="02" label="What we build" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Applications for Real Business Work.</h2>
          </Reveal>
          <div className="ca-after ca-bento">
            {apps.map(([number, kind, title, text], index) => (
              <Reveal key={title} delay={(index % 3) * 50} className={`ca-bento-item is-${kind} h-full`}>
                <article className="ca-card">
                  <div className="ca-card-top">
                    <span>{number}</span>
                    <ArrowRight className="ca-arrow w-4 h-4 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                  <Mini kind={kind} />
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="03" label="Business logic" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Where the Rules Become the Product.</h2>
          </Reveal>
          <div className="ca-after"><RulesEngine /></div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="04" label="Application architecture" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Connected From the Inside Out.</h2>
            <p className="ca-lead text-slate-600 leading-relaxed">
              The application is the system that holds the business rules, the people using it, and the services it can reach.
            </p>
          </Reveal>
          <div className="ca-after"><Architecture /></div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="05" label="Core capabilities" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built Around What Your Product Needs.</h2>
          </Reveal>
          <div className="ca-after ca-caps">
            {capabilities.map(([wide, kind, title, text], index) => (
              <Reveal key={title} delay={(index % 3) * 40} className={`ca-cap is-${kind} ${wide ? "is-wide" : ""} h-full`}>
                <article className="ca-cap-card">
                  <CapabilityMark kind={kind} />
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ca-section ca-build border-b border-slate-200">
        <div className="ca-wrap">
          <div className="ca-build-intro">
            <Reveal>
              <Kicker index="06" label="How we build" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Workflow to Working Software.</h2>
              <p className="ca-lead text-slate-600 leading-relaxed">
                Select a step. The active stage is the work in front of you, from the workflow through the release.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="07" label="The application experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Complex Behind the Scenes. Simple Up Front.</h2>
          </Reveal>
          <div className="ca-after"><AppStates /></div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200 bg-slate-50/70">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="08" label="Ready to evolve" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built for Today. Ready for What Comes Next.</h2>
          </Reveal>
          <div className="ca-after ca-evolve">
            <div className="ca-evolve-core">
              <small>Start</small>
              <strong>Core application</strong>
              <span>The workflow, the records, and the people who use them.</span>
            </div>
            <div className="ca-evolve-adds">
              {additions.map((label) => <span key={label}>{label}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="09" label="Why BizLogic" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Software That Fits the Business.</h2>
          </Reveal>
          <div className="ca-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {principles.map(([title, text, kind], index) => {
              const wide = index === 0 || index === principles.length - 1;
              return (
                <Reveal key={title} delay={(index % 2) * 70} className={wide ? "md:col-span-2" : ""}>
                  <article className={`ca-principle h-full border border-slate-200 bg-white p-5 sm:p-6 ${wide ? "md:grid md:grid-cols-[auto_1fr] md:gap-8 md:items-center" : ""}`}>
                    <PrincipleMark kind={kind} />
                    <div>
                      <h3 className="text-2xl font-semibold text-slate-900 font-heading">{title}</h3>
                      <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ca-section border-b border-slate-200">
        <div className="ca-wrap">
          <Reveal>
            <Kicker index="10" label="FAQ" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions About Custom Applications?</h2>
          </Reveal>
          <div className="ca-after"><FaqList /></div>
        </div>
      </section>

      <section className="ca-cta">
        <div className="ca-cta-grid" />
        <div className="ca-cta-glow" />
        <div className="ca-wrap relative ca-section">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Build the System Around Your Business.
          </h2>
          <p className="ca-lead text-slate-300 leading-relaxed">
            Tell us how your business works today, and we&apos;ll help turn the workflow into software that works the way you need it to.
          </p>
          <div className="ca-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
